import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { useEffect, lazy, Suspense } from "react";
import { disposeWorker } from "@/lib/utils/workerClient";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Legal from "./pages/Legal";

// Blog is content-only and standalone - lazy-load so it never weighs on the app.
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
// Visualize studio + shared-scene viewer (heavy: map). Lazy so / stays light.
const Visualize = lazy(() => import("./pages/Visualize"));
const SharedView = lazy(() => import("./pages/SharedView"));

const queryClient = new QueryClient();

/** Capture unhandled rejections + window errors at the top level. */
function GlobalErrorSinks() {
  useEffect(() => {
    const onErr = (e: ErrorEvent) => console.error("[window.error]", e.message, e.error);
    const onRej = (e: PromiseRejectionEvent) => console.error("[unhandledrejection]", e.reason);
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);

    // Free the geo worker's memory when the tab is backgrounded for a while.
    const onVisibility = () => { if (document.hidden) disposeWorker(); };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRej);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
  return null;
}

const App = () => (
  <ErrorBoundary scope="app">
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <a href="#main-content" className="skip-link">Skip to main content</a>
          <GlobalErrorSinks />
          <Toaster />
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <Routes>
              <Route path="/" element={<Index />} />
              {/* Deep links so each tool has its own crawlable URL (SEO). */}
              <Route path="/tools/:toolId" element={<Index />} />
              {/* Conversion landing pages (hub + per-pair) - SEO Fase 2. */}
              <Route path="/convert" element={<Index />} />
              <Route path="/convert/:pair" element={<Index />} />
              {/* Blog (topical authority) - lazy, wrapped in Suspense. */}
              <Route path="/blog" element={<Suspense fallback={null}><BlogIndex /></Suspense>} />
              <Route path="/blog/:slug" element={<Suspense fallback={null}><BlogPost /></Suspense>} />
              {/* Visualize studio + shared scene viewer (feat/visualize). */}
              <Route path="/visualize" element={<Suspense fallback={null}><Visualize /></Suspense>} />
              <Route path="/s/:token" element={<Suspense fallback={null}><SharedView /></Suspense>} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/roadmap" element={<Suspense fallback={null}><Roadmap /></Suspense>} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
